import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Tooltip,
  Typography,
  Alert,
  IconButton,
  Checkbox,
  COLORS,
} from 'pvs-design-system';
import { Box, Stack, styled } from '@mui/material';
import {
  ContentCopy,
  ModeEdit,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import getDisplayDate from 'utils/formatDate';
import { Link as LinkType, LinksQuery } from 'types';
import { useGetLinksListQuery } from 'api';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import bootConfig from 'utils/bootConfig';
import copyToClipboard from 'utils/copyToClipboard';
import BuildUrl from 'utils/formatLink';

const { isReadonlyLinks } = bootConfig.authInfo.permissions;

interface LinksTableProps {
  searchValue: string;
  selectedLinkIds: string[];
  onToggleLink: (link: LinkType, selected: boolean) => void;
  onToggleAllVisible: (links: LinkType[], selected: boolean) => void;
  canSelect: boolean;
  /** Controls whether the selection column (checkboxes) is shown at all. */
  showSelection?: boolean;
  /** Called when total link count from the API is available (for "Selected X of Y" display). */
  onTotalCountChange?: (total: number) => void;
}

const StyledModeEdit = styled(ModeEdit)({
  color: COLORS.purple[700],
});

const StyledModeView = styled(RemoveRedEyeIcon)({
  color: COLORS.purple[700],
});

const StyledContentCopy = styled(ContentCopy)({
  color: COLORS.purple[700],
});

const LinksTable: React.FC<LinksTableProps> = ({
  searchValue,
  selectedLinkIds,
  onToggleLink,
  onToggleAllVisible,
  canSelect,
  showSelection,
  onTotalCountChange,
}) => {
  const navigate = useNavigate();
  const [isSearching, setIsSearching] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortColumn, setSortColumn] = useState<
    | 'id'
    | 'name'
    | 'toolId'
    | 'created'
    | 'lastModified'
    | 'createdBy'
    | 'location'
  >('lastModified');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Sorting is not defined in the mockup and is currently hardcoded.
  const { currentData, data, isLoading, isFetching, isError, error } =
    useGetLinksListQuery(
      `?sortingDirection=${sortDirection}&` +
        `sortingType=${sortColumn}&` +
        `title=${searchValue ?? ''}&` +
        `pageNumber=${pageNumber + 1}&` +
        `pageSize=${pageSize}&` +
        `searchMode=${0}&` +
        `tool=${''}`
    ) as LinksQuery;

  useEffect(() => {
    setPageNumber(0);
    setIsSearching(true);
  }, [searchValue]);

  useEffect(() => {
    if (isSearching && !isFetching) {
      setIsSearching(false);
    }
  }, [isFetching, isSearching]);

  useEffect(() => {
    const total = isFetching ? data?.totalCount : currentData?.totalCount;
    if (total !== undefined && total !== null && onTotalCountChange) {
      onTotalCountChange(total);
    }
  }, [
    currentData?.totalCount,
    data?.totalCount,
    isFetching,
    onTotalCountChange,
  ]);

  const handleSort = (column: typeof sortColumn) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column with default direction
      setSortColumn(column);
      setSortDirection('asc');
    }
    setPageNumber(0); // Reset to first page when sorting
  };

  // Handle pagination page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPageNumber(newPage);
  };

  // Handle pagination rows per page change
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newPageSize = +parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    setPageNumber(0);
  };

  const renderSortIcon = (column: typeof sortColumn) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? (
      <ArrowUpward fontSize="small" />
    ) : (
      <ArrowDownward fontSize="small" />
    );
  };

  const currentPageLinks = data?.links ?? [];

  const selectedLinkIdsSet = useMemo(
    () => new Set(selectedLinkIds),
    [selectedLinkIds]
  );
  const allVisibleSelected =
    currentPageLinks.length > 0 &&
    currentPageLinks.every((link) => selectedLinkIdsSet.has(link.id));

  const someVisibleSelected =
    currentPageLinks.length > 0 &&
    currentPageLinks.some((link) => selectedLinkIdsSet.has(link.id)) &&
    !allVisibleSelected;

  return (
    <>
      <LoadingSpinner
        displayText="Loading LTI 1.3 Links"
        showLoading={isFetching && !currentData}
        useOverlay={isFetching && !isSearching && data !== undefined}
      />
      {!isLoading && !isSearching && (
        <>
          <Typography id="totalCount" variant="h5">
            {isFetching ? data?.totalCount || 0 : currentData?.totalCount || 0}{' '}
            {currentData?.totalCount === 1 ? 'link' : 'links'}
          </Typography>

          <TableContainer data-testid="links-table-container">
            <Table>
              <TableHead>
                <TableRow>
                  {showSelection && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        id="selectAllLinks"
                        checked={allVisibleSelected}
                        indeterminate={someVisibleSelected}
                        disabled={!canSelect || currentPageLinks.length === 0}
                        onChange={(event) =>
                          onToggleAllVisible(
                            currentPageLinks,
                            event.target.checked
                          )
                        }
                        inputProps={{ 'aria-label': 'Select all links' }}
                      />
                    </TableCell>
                  )}
                  <TableCell
                    onClick={() => handleSort('id')}
                    style={{ cursor: 'pointer' }}
                  >
                    Link ID {renderSortIcon('id')}
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('name')}
                    style={{ cursor: 'pointer' }}
                  >
                    Link Name {renderSortIcon('name')}
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('toolId')}
                    style={{ cursor: 'pointer' }}
                  >
                    Tool {renderSortIcon('toolId')}
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('location')}
                    style={{ cursor: 'pointer' }}
                  >
                    Location {renderSortIcon('location')}
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('createdBy')}
                    style={{ cursor: 'pointer' }}
                  >
                    Created By {renderSortIcon('createdBy')}
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('created')}
                    style={{ cursor: 'pointer' }}
                  >
                    Date Created {renderSortIcon('created')}
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('lastModified')}
                    style={{ cursor: 'pointer' }}
                  >
                    Date Last Modified {renderSortIcon('lastModified')}
                  </TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {!isError &&
                  currentPageLinks.map((item) => {
                    const displayLastModified = getDisplayDate(
                      item.lastModified
                    );
                    const displayCreatedDate = getDisplayDate(item.created);

                    return (
                      <TableRow key={item.id}>
                        {showSelection && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              id={`select_link_${item.id}`}
                              checked={selectedLinkIdsSet.has(item.id)}
                              disabled={!canSelect}
                              onChange={(event) =>
                                onToggleLink(item, event.target.checked)
                              }
                              inputProps={{
                                'aria-label': `Select link ${item.name}`,
                              }}
                            />
                          </TableCell>
                        )}
                        <TableCell sx={{ minWidth: '12rem' }}>
                          <Box title={item.id}>{item.id}</Box>
                        </TableCell>
                        <TableCell sx={{ minWidth: '12rem' }}>
                          <Box title={item.name}>{item.name}</Box>
                        </TableCell>
                        <TableCell sx={{ minWidth: '12rem' }}>
                          <Box title={item.configuration?.toolId}>
                            {item.configuration?.toolId}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box title={item.location}>{item.location}</Box>
                        </TableCell>
                        <TableCell>
                          <Box title={item.createdBy}>{item.createdBy}</Box>
                        </TableCell>
                        <TableCell>
                          <Box title={displayCreatedDate}>
                            {displayCreatedDate}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box title={displayLastModified}>
                            {displayLastModified}
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ padding: 0 }}>
                          <Tooltip
                            title={!isReadonlyLinks ? 'Edit Link' : 'View Link'}
                          >
                            <IconButton
                              id={`button_edit_link_${item.id}`}
                              aria-label={!isReadonlyLinks ? 'Edit' : 'View'}
                              onClick={() =>
                                navigate(`/ltimanagement/link/${item.id}/edit`)
                              }
                            >
                              {!isReadonlyLinks ? (
                                <StyledModeEdit />
                              ) : (
                                <StyledModeView />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Copy Link">
                            <IconButton
                              id={`button_copy_link_${item.id}`}
                              aria-label="Copy Link"
                              onClick={() =>
                                copyToClipboard(
                                  BuildUrl(
                                    bootConfig.ltiHandlerServiceBaseUrl,
                                    item.id
                                  )
                                )
                              }
                            >
                              <StyledContentCopy />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          {!isError && data?.links.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 100]}
              component="div"
              count={data?.totalCount || 0}
              rowsPerPage={pageSize}
              page={pageNumber}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}

          {isError && (
            <Box sx={{ marginTop: '32px' }}>
              {error?.originalStatus === 404 || error?.status === 404 ? (
                <Stack alignItems="center" data-testid="no-items-found">
                  <Typography variant="p-md">
                    No results found. Please try another search.
                  </Typography>
                </Stack>
              ) : (
                <Alert severity="error" data-testid="error">
                  We encountered an error loading LTI 1.3 links. Please try
                  again.
                </Alert>
              )}
            </Box>
          )}
        </>
      )}
    </>
  );
};

LinksTable.defaultProps = {
  onTotalCountChange: undefined,
  showSelection: true,
};

export default LinksTable;
